import {
  Box,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Text,
} from "@chakra-ui/react";
import { QuestionOutlineIcon } from "@chakra-ui/icons";

const DEFAULT_FIELDS =
  "created_at, name, email, status, origin, creator, work, works";
const DEFAULT_EXAMPLES = [
  "eagle tree",
  "creator = 'ron*'",
  "eagle or work = 'eagle*'",
  "email = '*gmail*'",
  "!(email = '*.hi.institute')",
  "created_at > '2026-04-01'",
  "origin = 'expo2026' and status = 'active'",
];

const DEFAULT_FREE_TEXT_SCOPE = "name, email, and profile";
const DEFAULT_FOOTER =
  "creator and work are cross-entity predicates (same scope on both pages); works counts APPROVED works only.";

type Props = {
  freeTextScope?: string;
  fields?: string;
  examples?: readonly string[];
  footer?: string;
};

export default function BooleanSearchHelp({
  freeTextScope = DEFAULT_FREE_TEXT_SCOPE,
  fields = DEFAULT_FIELDS,
  examples = DEFAULT_EXAMPLES,
  footer = DEFAULT_FOOTER,
}: Props = {}) {
  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <IconButton
          aria-label="Expression syntax help"
          size="sm"
          variant="ghost"
          icon={<QuestionOutlineIcon />}
        />
      </PopoverTrigger>
      <PopoverContent maxW="420px">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold">Expression syntax</PopoverHeader>
        <PopoverBody>
          <Stack spacing={3} fontSize="sm">
            <Box>
              <Text fontWeight="semibold">Free-text</Text>
              <Text color="gray.700">
                Bare words match across {freeTextScope}. Multiple terms are
                joined by AND: <code>eagle tree</code> finds rows containing
                both.
              </Text>
            </Box>
            <Box>
              <Text fontWeight="semibold">Fields</Text>
              <Text color="gray.700">{fields}</Text>
            </Box>
            <Box>
              <Text fontWeight="semibold">Operators</Text>
              <Text color="gray.700">
                = != &gt; &gt;= &lt; &lt;= and or not (!) parens
              </Text>
            </Box>
            <Box>
              <Text fontWeight="semibold">Wildcards (in quoted strings)</Text>
              <Text color="gray.700">
                * = zero or more chars · ? = exactly one char
              </Text>
            </Box>
            <Box>
              <Text fontWeight="semibold">Examples</Text>
              <Stack spacing={1} mt={1}>
                {examples.map((ex) => (
                  <Box
                    key={ex}
                    as="code"
                    fontFamily="mono"
                    fontSize="xs"
                    bg="gray.50"
                    px={2}
                    py={1}
                    borderRadius="sm"
                  >
                    {ex}
                  </Box>
                ))}
              </Stack>
            </Box>
            <Text color="gray.600" fontSize="xs">
              Field names, operators, and unquoted values are case-insensitive.{" "}
              {footer}
            </Text>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
